import React, { useState, useEffect, useCallback, useMemo } from "react";
import { IconButton, Card, CardContent, Typography, Box, CircularProgress, Badge, Tooltip } from '@mui/material';
import { useWebSocket, useUser } from '../WebSocketContext';
import { parseEventDate } from '../../Utils/dateUtils';
import Thumbnail from './Thumbnail';
import TimeEventViewMeta from './TimeEventViewMeta';
import TimeEventTags from "./TimeEventTags";
import TimeEventFilename from "./TimeEventFilename";
import TimeEventDateDisplay from "./TimeEventDateDisplay";
import MoreIcon from '@mui/icons-material/More';
import EventMenu from './TimeEventMenu';
import Attachments from './TimeEventAttachments';
import EventDetailsDialog from './EventDetailsDialog';
import { useSnackbar } from '../../Utils/SnackBarContext';
import { useDashboard } from '../Dashboard/DashboardContext';
import ErrorIcon from '@mui/icons-material/Error';
import EditedIcon from '../../images/edited.png';
import ConfirmedIcon from '../../images/check.png';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import HubIcon from '@mui/icons-material/Hub';
import FolderIcon from '@mui/icons-material/Folder';
import CallSplitIcon from '@mui/icons-material/CallSplit';
// used to mark linked-project events with a folder-style icon
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isErrorStatus, getErrorStatusTranslationKey } from '../../constants/timeEventStatus';
import { LINKED_PROJECT_PREFIX } from '../../constants/projectLink';
import { getPriorityTagDisplayValue } from './TimeEventTags';
import { fetchTimeeventById, reanalyzeTimeevent } from '../../Utils/timeEventsApi';
import GenericConnector from '../GenericConnector';
import SettingFoldersConnector from '../SettingFoldersConnector';
import { useFileViewer } from '../Viewer/FileViewerProvider';
import { isPendingSplitReview } from './timeEventReview';

// Add global styles for lightweight status animations
const globalStyles = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined' && !document.getElementById('time-event-styles')) {
  const style = document.createElement('style');
  style.id = 'time-event-styles';
  style.textContent = globalStyles;
  document.head.appendChild(style);
}

// Enhanced theme colors with better visual appeal
const primaryColor = '#6366f1'; // More modern indigo
const hoverColor = '#7c3aed'; // Rich purple
const lightPurple = '#f8faff'; // Softer background
const accentColor = '#f59e0b'; // Warm amber for highlights
const errorColor = '#ef4444'; // Modern red
const successColor = '#10b981'; // Modern green

const sourceChipStyles = {
  id: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
  },
  file: {
    backgroundColor: '#fff7ed',
    color: '#9a3412',
    border: '1px solid #fed7aa',
  },
  user: {
    backgroundColor: '#ecfdf5',
    color: '#047857',
    border: '1px solid #a7f3d0',
  },
  connector: {
    backgroundColor: '#eef2ff',
    color: '#3730a3',
    border: '1px solid #c7d2fe',
  },
  linked: {
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    color: '#4338ca',
    border: '1px solid rgba(99, 102, 241, 0.25)',
  },
};

const connectorIconFiles = {
  airtable: 'airtable.png',
  api: 'api.png',
  csv: 'csv.png',
  dropbox: 'dropbox.png',
  excel: 'excel.png',
  gmail: 'Gmail.png',
  gnews: 'gnews.png',
  googledrive: 'googledrive.png',
  mcp: 'mcp.png',
  mediastack: 'Mediastack.png',
  newsapi: 'newsapi.png',
  newsapiai: 'NewsAPIAI.png',
  notion: 'notion.png',
  onedrive: 'onedrive.png',
  outlookapi: 'outlookapi.png',
  outlooklocal: 'outlooklocal.png',
  pythonscript: 'PythonScript.png',
  salesforce: 'salesforce.png',
  slack: 'slack.png',
  test: 'test.png',
  webscraper: 'webscraper.png',
  whatsapp: 'whatsapp.png',
};

const normalizeConnectorIconKey = (value) => (
  String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
);

const getConnectorIconSrc = (type, name) => {
  const key = normalizeConnectorIconKey(type || name);
  const filename = connectorIconFiles[key] || (key ? `${key}.png` : '');
  return filename ? `/images/connectors/${filename}` : '';
};

const connectorTypeLabels = {
  airtable: 'Airtable',
  api: 'Generic API',
  csv: 'CSV',
  dropbox: 'Dropbox',
  excel: 'Excel',
  folders: 'Folders',
  gmail: 'Gmail',
  gnews: 'GNews',
  googledrive: 'Google Drive',
  mcp: 'MCP',
  mediastack: 'Mediastack',
  newsapi: 'NewsAPI',
  newsapiai: 'NewsAPI.ai',
  notion: 'Notion',
  onedrive: 'OneDrive',
  outlookapi: 'Outlook API',
  outlooklocal: 'Outlook',
  pythonscript: 'Python Script',
  salesforce: 'Salesforce',
  slack: 'Slack',
  test: 'Test Connector',
  webscraper: 'Web Scraper',
  whatsapp: 'Whatsapp',
};

const getConnectorTypeLabel = (type, fallback = '') => {
  const rawType = String(type || '').trim();
  const key = normalizeConnectorIconKey(rawType);
  if (connectorTypeLabels[key]) {
    return connectorTypeLabels[key];
  }

  const pluginLabel = rawType
    .replace(/^plugin_auto_/i, '')
    .replace(/_[0-9a-f]{6,}$/i, '')
    .replace(/_connector$/i, '')
    .replace(/_/g, ' ')
    .trim();

  if (pluginLabel) {
    return pluginLabel.replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  return fallback || rawType;
};

const getStableSourceHue = (value) => {
  const text = String(value || '').trim().toLowerCase();
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash) % 360;
};

const getConnectorSourceStyle = (value) => {
  const hue = getStableSourceHue(value);
  return {
    '--source-color': `hsl(${hue}, 64%, 42%)`,
    '--source-bg': `hsl(${hue}, 72%, 96%)`,
    '--source-border': `hsl(${hue}, 58%, 78%)`,
  };
};

const datasourceCacheByCase = new Map();
const datasourceRequestByCase = new Set();

const getDatasourceCache = (caseId) => {
  const key = String(caseId || '');
  if (!key) {
    return null;
  }
  if (!datasourceCacheByCase.has(key)) {
    datasourceCacheByCase.set(key, new Map());
  }
  return datasourceCacheByCase.get(key);
};

const findCachedDatasource = (caseId, datasourceId) => {
  const cache = getDatasourceCache(caseId);
  if (!cache || datasourceId === null || datasourceId === undefined) {
    return null;
  }
  return cache.get(String(datasourceId)) || null;
};


const TimeEvent = React.memo(({
  timeevent,
  showChartFunction,
  setFilteredTimeEvents,
  navigateTo,
  folder,
  hideDates = false,
  visibleTags = null,
  compact = false,
  maxVisibleTags = null,
  maxVisibleAttachments = null,
  enableTagAnimations = null,
  priorityTagVariable = null,
  onThumbnailSelect = null,
  preferThumbnailPreview = false,
  thumbnailIsSelected = false,
}) => {
  const { t } = useTranslation();
  const [currentTimeEvent, setCurrentTimeEvent] = useState(timeevent);
  const { id, case_id, eventdata, eventsource, eventtime, isExist, hide, parents, eventstatus, isUnread, isEdited, isConfirmed=0, fileCreationDate, username, uploader_name, datasource_id, postprocessing_state = 'ready', postprocessing_error, processing_error_message: processingErrorMessage } = currentTimeEvent;
  const attachmentLimit = maxVisibleAttachments ?? (compact ? 4 : null);
  const tagLimit = maxVisibleTags ?? (compact ? 6 : null);
  const tagAnimationsEnabled = enableTagAnimations != null ? enableTagAnimations : !compact;
  const isLinkedProjectEvent = typeof eventsource === 'string' && eventsource.startsWith(LINKED_PROJECT_PREFIX);
  const linkedProjectId = isLinkedProjectEvent ? eventsource.replace(LINKED_PROJECT_PREFIX, '') : null;
  const isEventProcessing = Number(eventstatus) === 1;
  const isPostProcessingActive = postprocessing_state === 'processing' && !isEventProcessing;
  const hasPendingSplitReview = isPendingSplitReview(currentTimeEvent);
  const isConfirmationDisabled = isEventProcessing || postprocessing_state === 'processing';
  const linkedProjectTitle = useMemo(() => {
    if (!isLinkedProjectEvent || typeof eventdata !== 'string') return '';
    const firstLine = eventdata.split('\n')[0] || '';
    const match = firstLine.match(/^(?:Linked project|Titre|Title)\s*:\s*(.+)$/i);
    return match && match[1] ? String(match[1]).trim() : '';
  }, [isLinkedProjectEvent, eventdata]);
const displayEventSource = isLinkedProjectEvent && linkedProjectTitle
    ? linkedProjectTitle
    : eventsource;
  const priorityTagDisplayValue = useMemo(() => {
    const normalizedPriorityTagVariable = (priorityTagVariable || '').trim();
    if (!normalizedPriorityTagVariable) {
      return '';
    }

    return getPriorityTagDisplayValue(currentTimeEvent?.customFields, normalizedPriorityTagVariable);
  }, [currentTimeEvent?.customFields, priorityTagVariable]);
  const shouldDisplayPriorityTagValue = Boolean((priorityTagVariable || '').trim());

  const normalizeDisplayName = useCallback((value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }, []);
  const uploaderDisplayName = normalizeDisplayName(uploader_name) || normalizeDisplayName(username);

  // State hooks
  const [isMetaVisible, setIsMetaVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { openSnackbar } = useSnackbar();
  // Custom hooks
  const { ws, sendMessage, subscribeToMessages } = useWebSocket();
  const { canManageProject } = useUser();
  const { setTimeEvent } = useDashboard();
  const { activeViewer } = useFileViewer();
  const isOpenInViewer = Boolean(activeViewer?.eventId && Number(activeViewer.eventId) === Number(id));
  const navigate = useNavigate();
  const [isCreatedTooltipOpen, setIsCreatedTooltipOpen] = useState(false);
  const [connectorDatasource, setConnectorDatasource] = useState(() => findCachedDatasource(case_id, datasource_id));
  const [isDatasourceDialogOpen, setIsDatasourceDialogOpen] = useState(false);
  const [shouldAutoOpenConnector, setShouldAutoOpenConnector] = useState(false);

  // Linked event dialog state
  const [dialogEvent, setDialogEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const requestedEventIdRef = React.useRef(null);

  const metaChipSx = {
    position: 'relative',
    padding: '3px 8px 3px 10px',
    borderRadius: '6px',
    fontSize: '0.67rem',
    fontWeight: 700,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.45,
    lineHeight: 1.45,
    maxWidth: 220,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxShadow: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 3,
      bottom: 3,
      left: 3,
      width: 3,
      borderRadius: '3px',
      backgroundColor: 'var(--source-color, currentColor)',
    },
  };
  const metaIconSx = { fontSize: '0.82rem', color: 'currentColor', flexShrink: 0 };

  // Helper function to update filtered time events
  const updateFilteredTimeEvents = useCallback((field, value) => {
    setFilteredTimeEvents((prev = []) => {
      if (!Array.isArray(prev)) return [];
      return prev.map(event =>
        event.id === id ? { ...event, [field]: value } : event
      );
    });
  }, [id, setFilteredTimeEvents]);
  const handleTagsChange = useCallback((nextTags) => {
    const normalizedTags = Array.isArray(nextTags) ? nextTags : [];
    setCurrentTimeEvent(prev => ({
      ...prev,
      customFields: normalizedTags,
    }));
    updateFilteredTimeEvents('customFields', normalizedTags);
  }, [updateFilteredTimeEvents]);
  const handleCreatedTooltipOpen = useCallback(() => {
    setIsCreatedTooltipOpen(true);
  }, []);
  const handleCreatedTooltipClose = useCallback(() => {
    setIsCreatedTooltipOpen(false);
  }, []);
  const handleCreatedTooltipToggle = useCallback(() => {
    setIsCreatedTooltipOpen(prev => !prev);
  }, []);


  const formatToLocalFriendly = useCallback((value) => {
    if (!value) return '';
    try {
      // Handle numeric epoch seconds
      if (typeof value === 'number' || String(value).match(/^\d+$/)) {
        const n = Number(value);
        const ms = n < 1e12 ? n * 1000 : n; // seconds -> ms, milliseconds stay ms
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleString();
      }
      const d = parseEventDate(value);
      if (d && !isNaN(d.getTime())) return d.toLocaleString();
    } catch (err) {
      // ignore and fall-through to empty
    }
    return '';
  }, []);

  // Update currentTimeEvent when timeevent prop changes
  useEffect(() => {
    setCurrentTimeEvent(timeevent);
  }, [timeevent]);

  useEffect(() => {
    setConnectorDatasource(findCachedDatasource(case_id, datasource_id));
  }, [case_id, datasource_id]);

  useEffect(() => {
    if (!canManageProject || !case_id || !datasource_id || typeof subscribeToMessages !== 'function') {
      return undefined;
    }

    const caseKey = String(case_id);
    const datasourceKey = String(datasource_id);
    const cachedDatasource = findCachedDatasource(case_id, datasource_id);
    if (cachedDatasource) {
      setConnectorDatasource(cachedDatasource);
      return undefined;
    }

    const unsubscribe = subscribeToMessages(['listGenericDatasources'], (parsedMessage = {}) => {
      const payload = parsedMessage?.data || {};
      if (payload.status === 'error' || !Array.isArray(payload.datasources)) {
        return;
      }

      const cache = getDatasourceCache(case_id);
      if (!cache) {
        return;
      }

      const connectorType = payload.type || '';
      payload.datasources.forEach((datasource) => {
        if (!datasource || datasource.id === null || datasource.id === undefined) {
          return;
        }
        cache.set(String(datasource.id), {
          ...datasource,
          type: datasource.type || connectorType,
        });
      });

      const resolvedDatasource = cache.get(datasourceKey);
      if (resolvedDatasource) {
        setConnectorDatasource(resolvedDatasource);
      }
    });

    if (!datasourceRequestByCase.has(caseKey) && typeof sendMessage === 'function') {
      datasourceRequestByCase.add(caseKey);
      sendMessage({
        command: 'listAllGenericDatasources',
        caseid: case_id,
      });
    }

    return unsubscribe;
  }, [canManageProject, case_id, datasource_id, sendMessage, subscribeToMessages]);

  // useEffect for websocket messages
  useEffect(() => {
    const watchedCommands = [
      "confirmEvent",
      "updateDateForEvent",
      "renalyzeTimeevent",
      "timeeventPostprocessingState"
    ];

    const handleWebSocketMessage = (parsedMessage = {}) => {
      const { command, data = {} } = parsedMessage;
      if (!command) {
        return;
      }

      if (command === "timeeventPostprocessingState") {
        const eventIds = Array.isArray(data.timeevent_ids) ? data.timeevent_ids : [];
        if (eventIds.some((eventId) => String(eventId) === String(id))) {
          setCurrentTimeEvent(prev => ({
            ...prev,
            postprocessing_state: data.state || prev.postprocessing_state,
            postprocessing_error: data.error || null,
          }));
          updateFilteredTimeEvents('postprocessing_state', data.state);
          updateFilteredTimeEvents('postprocessing_error', data.error || null);
        }
        return;
      }

      const targetId = data?.id ?? parsedMessage?.id;

      if (targetId == null || String(targetId) !== String(id)) {
        return;
      }

      switch (command) {
        case "confirmEvent":
          setCurrentTimeEvent(prev => ({ ...prev, isConfirmed: data.isConfirmed }));
          updateFilteredTimeEvents('isConfirmed', data.isConfirmed);
          openSnackbar("Event confirmed successfully", 3000);
          break;
        case "updateDateForEvent":
          updateFilteredTimeEvents('eventtime', data.data);
          openSnackbar("Date updated successfully", 3000);
          setCurrentTimeEvent(prev => ({ ...prev, eventtime: data.data }));
          break;
        case "renalyzeTimeevent":
          if (data.status === "success") {
            // Initial response: file queued for reanalysis
            console.log("Reanalyzing time event!!!!! id: " + id);
            // Set eventstatus to 1 (IN_PROGRESS) to show loading indicator
            setCurrentTimeEvent(prev => ({ ...prev, eventstatus: 1}));
            openSnackbar(data.message || "Time event queued for reanalysis", 3000);
          } else if (data.status === "error") {
            // Error response
            console.error("Error reanalyzing time event:", data.message);
            openSnackbar(data.message || "Error reanalyzing time event", 3000);
          } else if (data.data) {
            // Completion response: backend sends full event data after processing
            console.log("Time event processing completed:", data.data);
            setCurrentTimeEvent(prev => ({ ...prev, ...data.data }));
            // Update in dashboard context too
            if (setTimeEvent) {
              setTimeEvent(data.data);
            }
          }
          break;
        default:
          break;
      }
    };

    const unsubscribe = subscribeToMessages(watchedCommands, handleWebSocketMessage);
    return unsubscribe;
  }, [id, subscribeToMessages, setTimeEvent, updateFilteredTimeEvents, openSnackbar]);





  const handleReanalyze = useCallback(async () => {
    if (!canManageProject) {
      return;
    }
    const previousEventStatus = currentTimeEvent?.eventstatus;
    setCurrentTimeEvent(prev => ({ ...prev, eventstatus: 1 }));
    try {
      const response = await reanalyzeTimeevent(id);
      if (response?.status !== 'error') {
        openSnackbar(response?.message || "Time event queued for reanalysis", 3000);
        return;
      }

      setCurrentTimeEvent(prev => ({ ...prev, eventstatus: previousEventStatus }));
      openSnackbar(response?.message || "Error reanalyzing time event", 3000);
    } catch (error) {
      setCurrentTimeEvent(prev => ({ ...prev, eventstatus: previousEventStatus }));
      openSnackbar(error.message || "Error reanalyzing time event", 3000);
    }
  }, [canManageProject, currentTimeEvent?.eventstatus, id, openSnackbar]);

  const onToggleHide = useCallback(() => {
    if (!canManageProject) {
      return;
    }
    const newHideValue = hide === 1 ? 0 : 1;
    updateFilteredTimeEvents('hide', newHideValue);
    setCurrentTimeEvent(prev => ({ ...prev, hide: newHideValue }));
    sendMessage({ command: 'updateHideForEvent', id, hide: newHideValue });
  }, [canManageProject, hide, id, sendMessage, updateFilteredTimeEvents]);

  const handleMetaClick = useCallback(() => setIsMetaVisible(prev => !prev), []);
  const handleOpenLinkedProject = useCallback((targetCaseId) => {
    if (!targetCaseId) return;
    navigate(`/case/${targetCaseId}`);
  }, [navigate]);

  const handleShowLinkedEvent = useCallback(async (targetId) => {
    if (!targetId) return;
    requestedEventIdRef.current = targetId;
    try {
      const eventData = await fetchTimeeventById(targetId);
      if (eventData && eventData.id) {
        setDialogEvent(eventData);
        setIsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching linked event:', error);
    }
  }, []);

  const handleMenuOpen = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handleMenuClose = useCallback(() => setAnchorEl(null), []);  const handleConfirmEvent = useCallback(() => {
    if (!canManageProject || isConfirmationDisabled) {
      return;
    }
    // Optimistically update the UI immediately
    const newConfirmValue = isConfirmed === 1 ? 0 : 1; // Toggle the current value
    setCurrentTimeEvent(prev => ({ ...prev, isConfirmed: newConfirmValue }));
    updateFilteredTimeEvents('isConfirmed', newConfirmValue);
    
    sendMessage({ command: 'confirmEvent', id, confirm: newConfirmValue });
  }, [canManageProject, id, sendMessage, updateFilteredTimeEvents, isConfirmed, isConfirmationDisabled]);

  const connectorDisplayName = connectorDatasource?.name || connectorDatasource?.type || '';
  const connectorType = connectorDatasource?.type || '';
  const connectorTypeLabel = getConnectorTypeLabel(connectorType, connectorDisplayName);
  const connectorIconSrc = getConnectorIconSrc(connectorType, connectorDisplayName);
  const connectorSourceSx = useMemo(() => {
    if (!connectorDisplayName && !connectorType) {
      return {};
    }
    return {
      backgroundColor: 'var(--source-bg)',
      color: 'var(--source-color)',
      border: '1px solid var(--source-border)',
      ...getConnectorSourceStyle(connectorType || connectorDisplayName),
    };
  }, [connectorDisplayName, connectorType]);
  const handleOpenDatasourceDialog = useCallback((event) => {
    event?.stopPropagation?.();
    if (!connectorType || !canManageProject) {
      return;
    }
    setShouldAutoOpenConnector(true);
    setIsDatasourceDialogOpen(true);
  }, [canManageProject, connectorType]);

  return (
    <Box 
      position="relative" 
      width="100%" 
      mb={2}
      sx={{
        '&:hover': {
          '& .time-event-card': {
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.12), 0 3px 8px rgba(0, 0, 0, 0.08)',
          }
        }
      }}
    >
      <Badge
        component="div"
        badgeContent={t('timeevent.new', 'New')}
        sx={{
          display: 'block',
          width: '100%',
          position: 'relative',
          overflow: 'visible',
          "& .MuiBadge-badge": {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '0.7rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '0 6px',
            minWidth: 'unset',
            height: '18px',
            boxShadow: isUnread ? '0 1px 4px rgba(239, 68, 68, 0.25)' : 'none',
            top: { xs: 92, sm: 132 },
            left: 8,
            right: 'auto',
            transform: 'none',
            zIndex: 30,
          }
        }}
        invisible={!isUnread}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      >        <Box display="flex" alignItems="flex-start" sx={{ overflow: 'hidden', width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="center" sx={{ marginRight: { xs: 1, sm: 2 }, width: { xs: 60, sm: 100 }, minWidth: { xs: 60, sm: 100 }, flexShrink: 0 }}>
            {!hideDates && (
              <TimeEventDateDisplay
                initialDate={eventtime}
                editable={canManageProject && !shouldDisplayPriorityTagValue}
                onUpdateDate={(date) => {
                  if (!canManageProject) {
                    return;
                  }
                  if (date !== eventtime) {
                    ws.send(JSON.stringify({ command: 'updateDateForEvent', id, date }));
                    setCurrentTimeEvent(prev => ({ ...prev, eventtime: date, isEdited: 1 }));
                  }
                }}
                timeevent={currentTimeEvent}
                isExist={isExist}
                displayValue={shouldDisplayPriorityTagValue ? (priorityTagDisplayValue || '-') : null}
              />
            )}
            <Box sx={{ mt: 1, position: 'relative', width: '100%' }}>
              <Thumbnail 
                timeevent={currentTimeEvent}
                onOpenLinkedProject={handleOpenLinkedProject}
                onPreviewSelect={onThumbnailSelect}
                preferPreviewMode={preferThumbnailPreview}
                isSelected={thumbnailIsSelected || isOpenInViewer}
                sx={{ 
                  width: '100%',
                  height: { xs: 75, sm: 125 },
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s ease',
                  '&:hover': {
                    borderColor: primaryColor + '60',
                  }
                }} 
              />
              {!isExist && !isLinkedProjectEvent && (
                <Tooltip title="File not found">
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(239, 68, 68, 0.95)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(239, 68, 68, 0.25)',
                      border: '2px solid white',
                    }}
                  >
                    <ErrorIcon sx={{ color: 'white', fontSize: '14px' }} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box><Card 
            className="time-event-card"
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              flexGrow: 1, 
              boxShadow: '0 2px 6px rgba(99, 102, 241, 0.08)',
              overflow: 'hidden',
              border: `1px solid ${isConfirmed === 1 ? primaryColor + '40' : 'rgba(226, 232, 240, 0.8)'}`,
              backgroundColor: isConfirmed === 1
                ? `linear-gradient(135deg, ${lightPurple} 0%, #f1f5f9 100%)` 
                : `linear-gradient(135deg, #ffffff 0%, #fafbff 100%)`,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              borderRadius: '12px', 
              maxWidth: '100%',
              transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                
                right: 0,
                height: '3px',
                background: isConfirmed === 1
                  ? `linear-gradient(90deg, ${successColor} 0%,${hoverColor} 100%)`
                  : 'transparent',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
              },
              '&:hover': {
                borderColor: primaryColor + '60',
                '& .time-event-filename': {
                  color: primaryColor,
                },
                '& .time-event-menu-btn': {
                  opacity: 1,
                  transform: 'scale(1)',
                }
              }
            }}
          >            <CardContent 
              sx={{ 
                flexGrow: 1, 
                p: "16px", 
                overflow: 'hidden', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'flex-start',
                gap: '12px',
                '&:last-child': {
                  paddingBottom: '16px',
                }
              }}
            >              <Box display="flex" flexDirection="column" width="100%">
                <Box 
                  display="flex" 
                  flexWrap="wrap" 
                  alignItems="center" 
                  mb={0.5} 
                  sx={{ 
                    overflow: { xs: 'visible', md: 'hidden' }, 
                    maxWidth:'100%'
                  }}
                >
                  <Box 
                    display="flex" 
                    flex="1 1 auto" 
                    minWidth={0} 
                    alignItems="center" 
                    gap={1.5}
                    sx={{ 
                      overflow: { xs: 'visible', md: 'hidden' },
                      flexWrap: 'wrap',
                      rowGap: { xs: 0.5, md: 0 }
                    }}
                  >
                    <TimeEventFilename 
                      path={displayEventSource} 
                      folder={folder} 
                      aiTokenUsage={currentTimeEvent?.ai_token_usage}
                      className="time-event-filename"
                      sx={{ 
                        flexShrink: 1, 
                        minWidth: 0, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        maxWidth: { xs: '100%', sm: '50%' },
                        color: '#374151', 
                        fontWeight: '600',
                        fontSize: '0.9rem', 
                        transition: 'color 0.2s ease'
                      }} 
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        rowGap: { xs: 0.5, md: 0 },
                        maxWidth: '100%',
                        minWidth: 0,
                        flex: '1 1 220px'
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ ...metaChipSx, ...sourceChipStyles.id }}
                      >
                        ID: {id}
                      </Typography>
                      {isLinkedProjectEvent && (
                        <Typography
                          variant="caption"
                          sx={{
                            ...metaChipSx,
                            ...sourceChipStyles.linked,
                          }}
                        >
                          <FolderIcon sx={metaIconSx} />
                          {t('timeevent.linked_project_short', 'Linked')} #{linkedProjectId || '?'}
                        </Typography>
                      )}
                      {fileCreationDate && (
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', fontWeight: 600 }}
                              >
                                {t('timeevent.created', 'Created')} (file creation time)
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', opacity: 0.85 }}
                              >
                                {formatToLocalFriendly(fileCreationDate)}
                              </Typography>
                            </Box>
                          }
                          arrow
                          open={isCreatedTooltipOpen}
                          onOpen={handleCreatedTooltipOpen}
                          onClose={handleCreatedTooltipClose}
                          enterTouchDelay={0}
                          leaveTouchDelay={2000}
                        >
                          <Typography
                            variant="caption"
                            sx={{ ...metaChipSx, ...sourceChipStyles.file, cursor: 'pointer' }}
                            onClick={handleCreatedTooltipToggle}
                          >
                            <AccessTimeIcon sx={metaIconSx} />
                            {formatToLocalFriendly(fileCreationDate)}
                          </Typography>
                        </Tooltip>
                      )}
                      {connectorDisplayName ? (
                        <Tooltip title={t('timeevent.open_connector', 'Open connector')} arrow>
                          <Typography
                            variant="caption"
                            sx={{
                              ...metaChipSx,
                              ...connectorSourceSx,
                              cursor: canManageProject ? 'pointer' : 'default',
                              '&:hover': canManageProject ? {
                                filter: 'saturate(1.08) brightness(0.98)',
                              } : {}
                            }}
                            onClick={handleOpenDatasourceDialog}
                          >
                            {connectorIconSrc ? (
                              <Box
                                component="img"
                                src={connectorIconSrc}
                                alt=""
                                onError={(event) => {
                                  event.currentTarget.style.display = 'none';
                                  const fallback = event.currentTarget.nextElementSibling;
                                  if (fallback) {
                                    fallback.style.display = 'inline-block';
                                  }
                                }}
                                sx={{
                                  width: 13,
                                  height: 13,
                                  objectFit: 'contain',
                                  flexShrink: 0,
                                }}
                              />
                            ) : null}
                            <HubIcon sx={{ ...metaIconSx, display: connectorIconSrc ? 'none' : 'inline-block' }} />
                            {connectorDisplayName}
                          </Typography>
                        </Tooltip>
                      ) : uploaderDisplayName && (
                        <Typography
                          variant="caption"
                          sx={{ ...metaChipSx, ...sourceChipStyles.user }}
                        >
                          <PersonIcon sx={metaIconSx} />
                          {uploaderDisplayName}
                        </Typography>
                      )}
                    </Box>
                    {parents?.map((parent, index) => (
                      <Tooltip key={parent} title={t('timeevent.view_parent', 'View parent details')} placement="top">
                        <IconButton
                          onClick={() => handleShowLinkedEvent(parent)}
                          sx={{
                            backgroundColor: `${primaryColor}10`,
                            margin: '0 4px',
                            color: primaryColor,
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: `1px solid ${primaryColor}20`,
                            '&:hover': {
                              backgroundColor: primaryColor,
                              color: 'white',
                              boxShadow: `0 2px 8px ${primaryColor}24`,
                            },
                            transition: 'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                          }}
                        >
                          <EscalatorWarningIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                  {hasPendingSplitReview && (
                    <Tooltip
                      title={t('timeevent.split_review_pending_tooltip')}
                      placement="top"
                      arrow
                    >
                      <Box
                        role="status"
                        aria-label={t('timeevent.split_review_pending_tooltip')}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.65,
                          flexShrink: 0,
                          ml: 'auto',
                          px: 0.85,
                          py: 0.45,
                          borderRadius: '999px',
                          color: '#9a3412',
                          backgroundColor: '#fff7ed',
                          border: '1px solid #fed7aa',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: '100%', sm: 180 },
                        }}
                      >
                        <CallSplitIcon sx={{ fontSize: 15 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'inherit',
                            fontSize: '0.68rem',
                            lineHeight: 1.2,
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {t('timeevent.split_review_pending_short')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  {(isEventProcessing || isPostProcessingActive) && (
                    <Tooltip
                      title={isEventProcessing
                        ? t('timeevent.loading_processing')
                        : t('timeevent.postprocessing_status_tooltip')}
                      placement="top"
                      arrow
                    >
                      <Box
                        role="status"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.65,
                          flexShrink: 0,
                          ml: 'auto',
                          px: 0.85,
                          py: 0.45,
                          borderRadius: '999px',
                          color: isEventProcessing ? '#4338ca' : '#0369a1',
                          backgroundColor: isEventProcessing ? '#eef2ff' : '#f0f9ff',
                          border: isEventProcessing
                            ? '1px solid #c7d2fe'
                            : '1px solid #bae6fd',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: '100%', sm: 150 },
                        }}
                      >
                        <CircularProgress
                          size={13}
                          thickness={4}
                          sx={{ color: 'currentColor' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'inherit',
                            fontSize: '0.68rem',
                            lineHeight: 1.2,
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {isEventProcessing
                            ? t('timeevent.loading_processing')
                            : t('timeevent.postprocessing_status_short')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                  
                </Box>
              </Box>
              <Attachments 
                timeevent={currentTimeEvent} 
                navigateTo={navigateTo} 
                onAttachmentClick={handleShowLinkedEvent}
                maxVisible={attachmentLimit}
              />
              <TimeEventTags
                timeevent={currentTimeEvent}
                showChartFunction={showChartFunction}
                onToggleRenalyze={handleReanalyze}
                canManageProject={canManageProject}
                visibleTags={visibleTags}
                maxVisibleTags={tagLimit}
                priorityTagVariable={priorityTagVariable}
                enableAnimations={tagAnimationsEnabled}
                onTagsChange={handleTagsChange}
              />
              {isMetaVisible && <TimeEventViewMeta timeevent={currentTimeEvent} editable={canManageProject} />}
            </CardContent>
          </Card>
          
          {/* Menu Button moved outside CardContent to same level as loading overlay */}
          <IconButton
            className="time-event-menu-btn"
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 20, // Much higher than loading overlay (10) and menu background (5)
              color: '#6b7280',
              opacity: eventstatus === 1 ? 1 : 0.7, // More visible during loading
              transition: 'background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease',
              backgroundColor: eventstatus === 1 
                ? 'rgba(255, 255, 255, 1)' // Fully opaque during loading
                : 'rgba(255, 255, 255, 0.95)',
              border: eventstatus === 1 
                ? `2px solid ${primaryColor}40` // Colored border during loading
                : '1px solid rgba(226, 232, 240, 0.5)',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              pointerEvents: 'auto', // Ensure it remains clickable
              boxShadow: eventstatus === 1 
                ? `0 2px 10px ${primaryColor}20`
                : '0 1px 4px rgba(0, 0, 0, 0.08)',
              '&:hover': {
                backgroundColor: primaryColor,
                color: 'white',
                opacity: 1,
                boxShadow: `0 2px 8px ${primaryColor}24`,
              },
            }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
          
          <EventMenu
            anchorEl={anchorEl}
            handleMenuClose={handleMenuClose}
            handleMetaClick={handleMetaClick}
            onToggleHide={onToggleHide}
            onToggleRenalyze={handleReanalyze}
            timeevent={currentTimeEvent}
            handleConfirmEvent={handleConfirmEvent}
            canManageProject={canManageProject}
            sx={{ zIndex: 25 }} // Ensure menu appears above all loading elements
          />
          {connectorType && isDatasourceDialogOpen && (
            String(connectorType).toLowerCase() === 'folders' ? (
              <SettingFoldersConnector
                caseid={case_id}
                hideLauncher={true}
                forceOpen={shouldAutoOpenConnector}
                onOpen={() => setShouldAutoOpenConnector(false)}
                onConnectorClosed={() => {
                  setShouldAutoOpenConnector(false);
                  setIsDatasourceDialogOpen(false);
                }}
              />
            ) : (
              <GenericConnector
                caseid={case_id}
                type={connectorType}
                title={connectorTypeLabel || connectorType || connectorDisplayName}
                useImageIcon={true}
                hideLauncher={true}
                forceOpen={shouldAutoOpenConnector}
                forceOpenDatasourceId={datasource_id}
                onOpen={() => setShouldAutoOpenConnector(false)}
                onConnectorClosed={() => {
                  setShouldAutoOpenConnector(false);
                  setIsDatasourceDialogOpen(false);
                }}
              />
            )
          )}
        </Box>
        
      </Badge>
        {/* Enhanced edited icon with modern styling */}
      {isEdited === 1 && !eventdata?.tag_isedited && (
        <Tooltip title="User has edited this event. This event will not no longer be updated by the system until you force an analyze on this event.">
          <Box
            position="absolute"
            bottom={12}
            right={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="12px"
            bgcolor="rgba(255, 255, 255, 0.95)"
            boxShadow="0 2px 8px rgba(245, 158, 11, 0.18)"
            border={`2px solid ${accentColor}20`}
            p={1}
            zIndex={20}
            sx={{
              transition: 'box-shadow 0.15s ease',
              '&:hover': {
                boxShadow: `0 3px 10px ${accentColor}24`,
              }
            }}
          >
            <img
              src={EditedIcon}
              alt="Edited"
              style={{
                width: '20px',
                height: '20px',
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
              }}
            />
          </Box>
        </Tooltip>
      )}

      {/* Enhanced confirmed icon with modern styling */}
      {isConfirmed === 1 && !eventdata?.tag_confirmed && (
        <Tooltip title="This event has been confirmed">
          <Box
            position="absolute"
            bottom={12}
            right={isEdited === 1 && !eventdata?.tag_isedited ? 58 : 12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="12px"
            bgcolor="rgba(255, 255, 255, 0.95)"
            boxShadow="0 2px 8px rgba(16, 185, 129, 0.18)"
            border={`2px solid ${successColor}20`}
            p={1}
            zIndex={20}
            sx={{
              transition: 'box-shadow 0.15s ease',
              '&:hover': {
                boxShadow: `0 3px 10px ${successColor}24`,
              }
            }}
          >
            <img
              src={ConfirmedIcon}
              alt="Confirmed"
              style={{
                width: '20px',
                height: '20px',
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
              }}
            />
          </Box>
        </Tooltip>
      )}
      {postprocessing_state === 'ready_with_errors' && !isErrorStatus(eventstatus) && (
        <Tooltip title={postprocessing_error || t('timeevent.postprocessing_completed_with_errors')} placement="right">
          <Box
            position="absolute"
            top={12}
            left={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="10px"
            bgcolor="rgba(255, 255, 255, 0.96)"
            border={`2px solid ${accentColor}40`}
            p={1}
            zIndex={20}
          >
            <ErrorIcon sx={{ color: accentColor, fontSize: 20 }} />
          </Box>
        </Tooltip>
      )}
      {/* Enhanced error icon with modern styling and better positioning */}
      {isErrorStatus(eventstatus) && (
        <Tooltip 
          title={processingErrorMessage ? (
            <Box sx={{ maxWidth: 360 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t(getErrorStatusTranslationKey(eventstatus))}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5, whiteSpace: 'normal' }}>
                {processingErrorMessage}
              </Typography>
            </Box>
          ) : t(getErrorStatusTranslationKey(eventstatus))}
          placement="right"
        >
          <Box
            position="absolute"
            top={12}
            left={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="12px"
            bgcolor="rgba(255, 255, 255, 0.95)"
            boxShadow="0 2px 8px rgba(239, 68, 68, 0.2)"
            border={`2px solid ${errorColor}20`}
            p={1.5}
            zIndex={20}
            sx={{ 
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease',
              '&:hover': {
                boxShadow: `0 3px 10px ${errorColor}24`,
              }
            }}
          >
            <ErrorIcon 
              sx={{ 
                color: errorColor, 
                fontSize: 22,
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
              }} 
            />
          </Box>
        </Tooltip>
      )}
      <EventDetailsDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        event={dialogEvent}
      />
      </Box>
  );
});

export default TimeEvent;
